import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import {
  Search as SearchIcon, Filter, Globe, Mail, Phone, UserCheck, RefreshCw,
  ExternalLink, Building2, Sparkles, X, Eye, Zap, Download, Trash2, Upload,
  ShieldCheck
} from "lucide-react";
import CrmCompanyCard from "../components/CrmCompanyCard";
import CsvImportModal from "../components/CsvImportModal";

const fmt = (n) => (n == null ? "—" : new Intl.NumberFormat("it-IT").format(n));

export default function Crm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    province: "",
    has_website: "",
    has_email: "",
    has_owner: "",
    in_list: params.get("inList") || "",
  });
  const [selectedId, setSelectedId] = useState(null);
  const [deducing, setDeducing] = useState(false);
  const [checked, setChecked] = useState({});
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  const load = useCallback(async (newSkip = 0) => {
    setLoading(true);
    try {
      const p = { ...filters, skip: newSkip, limit: 50 };
      Object.keys(p).forEach(k => p[k] === "" && delete p[k]);
      const { data } = await api.get("/crm/companies", { params: p });
      setCompanies(data.companies || []);
      setTotal(data.total || 0);
      setSkip(newSkip);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(0); }, [load]);

  const upd = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const deduceAll = async () => {
    setDeducing(true);
    try {
      const { data } = await api.post("/crm/deduce-websites");
      alert(`${data.deduced} siti dedotti dalle email. Ricarico la lista.`);
      await load(0);
    } finally { setDeducing(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/export/companies.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "leadgen_crm_full.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Errore export: " + (e.response?.data?.detail || e.message));
    }
  };

  const checkedIds = Object.keys(checked).filter(k => checked[k]);
  const allChecked = companies.length > 0 && companies.every(c => checked[c.id]);
  const toggleAll = () => {
    if (allChecked) setChecked({});
    else setChecked(Object.fromEntries(companies.map(c => [c.id, true])));
  };
  const toggleOne = (id, e) => {
    e.stopPropagation();
    setChecked(s => ({...s, [id]: !s[id]}));
  };

  const [estimateModal, setEstimateModal] = useState(null); // {estimate, includeStakeholders}

  const [findModal, setFindModal] = useState(null);
  const [sourcesStatus, setSourcesStatus] = useState(null);
  const [useOpenapiFallback, setUseOpenapiFallback] = useState(false);

  useEffect(() => {
    api.get("/crm/web-search/status").then(r => setSourcesStatus(r.data)).catch(()=>{});
  }, []);

  const requestFindContacts = async () => {
    if (checkedIds.length === 0) return;
    setEnriching(true);
    try {
      const { data } = await api.post("/crm/companies/find-contacts-estimate", {
        companyIds: checkedIds,
        useOpenapiFallback,
      });
      setFindModal({ estimate: data });
    } finally { setEnriching(false); }
  };

  const confirmFindContacts = async () => {
    setFindModal(null);
    setEnriching(true); setEnrichResult(null);
    try {
      const { data } = await api.post("/crm/companies/find-contacts", {
        companyIds: checkedIds,
        useOpenapiFallback,
      });
      setEnrichResult({ ...data, _findContacts: true });
      setChecked({});
      await load(skip);
    } finally { setEnriching(false); }
  };

  const requestBulkEnrich = async (includeStakeholders) => {
    if (checkedIds.length === 0) return;
    setEnriching(true);
    try {
      const { data } = await api.post("/crm/companies/bulk-enrich-estimate", {
        companyIds: checkedIds,
        includeStakeholders,
      });
      setEstimateModal({ estimate: data, includeStakeholders });
    } finally { setEnriching(false); }
  };

  const confirmBulkEnrich = async () => {
    const includeStakeholders = estimateModal?.includeStakeholders;
    setEstimateModal(null);
    setEnriching(true); setEnrichResult(null);
    try {
      const { data } = await api.post("/crm/companies/bulk-enrich", {
        companyIds: checkedIds,
        includeStakeholders,
      });
      setEnrichResult(data);
      setChecked({});
      await load(skip);
    } finally { setEnriching(false); }
  };

  const bulkScrape = async () => {
    if (checkedIds.length === 0) return;
    if (!window.confirm(`Scraping GRATUITO di ${checkedIds.length} siti web per cercare email di contatto?\n\nNessun credito OpenAPI verrà consumato.`)) return;
    setEnriching(true); setEnrichResult(null);
    try {
      const { data } = await api.post("/crm/companies/bulk-scrape", {
        companyIds: checkedIds,
      });
      setEnrichResult({ ...data, _scrape: true });
      setChecked({});
      await load(skip);
    } finally { setEnriching(false); }
  };

  const deleteOne = async (companyId, companyName) => {
    if (!window.confirm(
      `Eliminare definitivamente "${companyName}" dal CRM?\n\n` +
      `Verranno cancellate anche tutte le sue analisi, le membership nelle liste e le email inviate. ` +
      `L'azione NON è reversibile.`
    )) return;
    try {
      await api.delete(`/crm/companies/${companyId}`);
      await load(skip);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const bulkDelete = async () => {
    if (checkedIds.length === 0) return;
    if (!window.confirm(
      `Eliminare definitivamente ${checkedIds.length} aziende dal CRM?\n\n` +
      `Verranno cancellate anche tutte le loro analisi, le membership nelle liste e le email inviate. ` +
      `L'azione NON è reversibile.`
    )) return;
    setEnriching(true);
    try {
      const { data } = await api.post("/crm/companies/bulk-delete", { ids: checkedIds });
      setEnrichResult({ _delete: true, deleted: data.deleted });
      setChecked({});
      await load(skip);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setEnriching(false); }
  };

  const bulkVerifyEmail = async () => {
    if (checkedIds.length === 0) return;
    if (!window.confirm(
      `Verificare la deliverability delle email per ${checkedIds.length} aziende selezionate via ZeroBounce?\n\n` +
      `Costo stimato: ~${(checkedIds.length * 0.0065).toFixed(2)} USD (le verifiche recenti <60gg vengono riusate dalla cache).`
    )) return;
    setEnriching(true); setEnrichResult(null);
    try {
      const { data } = await api.post("/email/verify/bulk", { company_ids: checkedIds });
      setEnrichResult({ _verify: true, ...data });
      await load(skip);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setEnriching(false); }
  };

  const stats = {
    withWebsite: companies.filter(c => c.website).length,
    withEmail: companies.filter(c => c.email).length,
    withOwner: companies.filter(c => c.owner).length,
    inLists: companies.filter(c => c.inListsCount > 0).length,
  };

  return (
    <div data-testid="crm-page">
      <div className="flex justify-between items-center mb-6" style={{flexWrap:"wrap",gap:12}}>
        <div>
          <div className="h-display">CRM</div>
          <div className="text-muted mt-2">Database completo di tutte le aziende estratte da OpenAPI · {fmt(total)} aziende totali</div>
        </div>
        <div className="flex gap-2">
          {checkedIds.length > 0 && (
            <>
              <button onClick={requestFindContacts} disabled={enriching} className="btn btn-primary btn-sm" data-testid="find-contacts-btn"
                title="Catena automatica: OpenAPI IT-Marketing (solo se serve) → deduce dominio dall'email → scrape gratuito del sito. È il modo migliore per recuperare email VERE (non PEC) quando l'unico dato disponibile è il PEC.">
                {enriching ? <span className="spinner"/> : <><Sparkles size={14}/> Trova contatti completi ({checkedIds.length})</>}
              </button>
              <button onClick={bulkScrape} disabled={enriching} className="btn btn-ghost btn-sm" data-testid="bulk-scrape-btn"
                title="GRATIS: scrappa SOLO i siti già conosciuti. Salta aziende senza website (PEC non basta). Per recuperare anche le email mancanti usa 'Trova contatti completi' invece.">
                {enriching ? <span className="spinner"/> : <><Globe size={14}/> Solo scrape siti noti ({checkedIds.length})</>}
              </button>
              <button onClick={()=>requestBulkEnrich(false)} disabled={enriching} className="btn btn-secondary btn-sm" data-testid="bulk-enrich-marketing-btn"
                title="A PAGAMENTO: chiama OpenAPI IT-Marketing per email/sito/telefono. Le aziende già arricchite (cache) vengono saltate.">
                {enriching ? <span className="spinner"/> : <><Mail size={14}/> Solo OpenAPI email/sito ({checkedIds.length})</>}
              </button>
              <button onClick={()=>requestBulkEnrich(true)} disabled={enriching} className="btn btn-secondary btn-sm" data-testid="bulk-enrich-full-btn"
                title="A PAGAMENTO: chiama OpenAPI IT-Marketing + IT-Stakeholders. Le aziende già arricchite vengono saltate.">
                {enriching ? <span className="spinner"/> : <><Zap size={14}/> OpenAPI + titolari ({checkedIds.length})</>}
              </button>
              <button onClick={bulkVerifyEmail} disabled={enriching} className="btn btn-secondary btn-sm" data-testid="bulk-verify-btn"
                title="Verifica deliverability email via ZeroBounce. Le verifiche <60gg vengono riusate. Necessita ZEROBOUNCE_API_KEY.">
                {enriching ? <span className="spinner"/> : <><ShieldCheck size={14}/> Verifica email ({checkedIds.length})</>}
              </button>
              <button onClick={bulkDelete} disabled={enriching} className="btn btn-ghost btn-sm" data-testid="bulk-delete-btn"
                title="Elimina definitivamente le aziende selezionate dal CRM (cascata: liste, analisi, email inviate)."
                style={{color:"var(--mv-danger)",borderColor:"var(--mv-danger)"}}>
                <Trash2 size={14}/> Elimina ({checkedIds.length})
              </button>
            </>
          )}
          <button onClick={()=>setShowCsvImport(true)} className="btn btn-primary btn-sm" data-testid="csv-import-btn"
            title="Importa una lista di contatti da CSV (es. liste acquistate da PagineGialle, fornitori, export LinkedIn).">
            <Upload size={14}/> Importa CSV
          </button>
          <button onClick={deduceAll} disabled={deducing} className="btn btn-secondary btn-sm" data-testid="deduce-websites-btn"
            title="Deduce il dominio del sito web dall'email aziendale (escludendo provider generici come gmail, libero, pec.it…)">
            {deducing ? <span className="spinner"/> : <><Sparkles size={14}/> Deduci siti dalle email</>}
          </button>
          <button onClick={exportCsv} className="btn btn-ghost btn-sm" data-testid="export-crm-csv-btn"
            title="Scarica un CSV con tutte le aziende del CRM (66 oggi) per backup o import in altri tool.">
            <Download size={14}/> Export CSV
          </button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid gap-3 mb-6" style={{gridTemplateColumns:"repeat(auto-fit, minmax(180px,1fr))"}}>
        <div className="card" style={{padding:14}}>
          <div className="stat-label">Mostrate</div>
          <div className="stat-num">{companies.length}</div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label flex items-center gap-2"><Globe size={12}/> Con sito web</div>
          <div className="stat-num" style={{color:"var(--mv-success)"}}>{stats.withWebsite}</div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label flex items-center gap-2"><Mail size={12}/> Con email</div>
          <div className="stat-num" style={{color:"var(--mv-primary)"}}>{stats.withEmail}</div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label flex items-center gap-2"><UserCheck size={12}/> Con titolare</div>
          <div className="stat-num">{stats.withOwner}</div>
        </div>
        <div className="card" style={{padding:14}}>
          <div className="stat-label flex items-center gap-2"><Building2 size={12}/> In una lista</div>
          <div className="stat-num">{stats.inLists}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4" style={{padding:16}}>
        <div className="grid gap-3" style={{gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr"}}>
          <div style={{position:"relative"}}>
            <SearchIcon size={16} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"var(--mv-text-muted)"}}/>
            <input className="input" placeholder="Ragione sociale, P.IVA, email, città…"
              value={filters.q} onChange={e=>upd("q", e.target.value)}
              style={{paddingLeft:38}} data-testid="crm-search"/>
          </div>
          <input className="input" placeholder="Provincia (MI, LO, RM…)"
            value={filters.province} onChange={e=>upd("province", e.target.value.toUpperCase())} data-testid="crm-province"/>
          <select className="select" value={filters.has_website} onChange={e=>upd("has_website", e.target.value)} data-testid="crm-has-website">
            <option value="">🌐 Sito (tutti)</option>
            <option value="true">Solo con sito</option>
            <option value="false">Senza sito</option>
          </select>
          <select className="select" value={filters.has_email} onChange={e=>upd("has_email", e.target.value)}>
            <option value="">✉️ Email (tutti)</option>
            <option value="true">Solo con email</option>
            <option value="false">Senza email</option>
          </select>
          <select className="select" value={filters.in_list} onChange={e=>upd("in_list", e.target.value)} data-testid="crm-in-list">
            <option value="">📋 Lista (tutti)</option>
            <option value="true">In una lista</option>
            <option value="false">Mai aggiunte</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{padding:0,overflow:"auto"}}>
        {loading && <div style={{padding:20,textAlign:"center"}}><span className="spinner"/></div>}
        {!loading && companies.length === 0 && (
          <div style={{padding:60,textAlign:"center"}} className="text-muted">Nessuna azienda corrisponde ai filtri</div>
        )}
        {!loading && companies.length > 0 && (
          <table className="tbl" data-testid="crm-table">
            <thead>
              <tr>
                <th style={{width:30}}><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="crm-select-all"/></th>
                <th>Azienda</th>
                <th>Titolare</th>
                <th>Sito web</th>
                <th>Email</th>
                <th>Telefono</th>
                <th>Fatturato</th>
                <th>In liste</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.id} onClick={()=>setSelectedId(c.id)} style={{cursor:"pointer"}} data-testid={`crm-row-${c.vatCode}`}>
                  <td onClick={e=>e.stopPropagation()}>
                    <input type="checkbox" checked={!!checked[c.id]} onChange={e=>toggleOne(c.id, e)} data-testid={`crm-check-${c.vatCode}`}/>
                  </td>
                  <td>
                    <div style={{fontWeight:600}}>{c.companyName}</div>
                    <div className="text-small text-muted">
                      {c.city ? `${c.city} (${c.province})` : ""} · P.IVA {c.vatCode}
                    </div>
                  </td>
                  <td className="text-small">
                    {c.owner?.fullName ? (
                      <div>
                        <div style={{fontWeight:600,color:"var(--mv-primary)"}}>{c.owner.fullName}</div>
                        {c.owner.role && <div className="text-muted" style={{fontSize:11}}>{c.owner.role.split(',')[0]}</div>}
                      </div>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-small">
                    {c.website ? (
                      <div>
                        <a href={`https://${c.website.replace(/^https?:\/\//,"")}`} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}>
                          {c.website}
                        </a>
                        {c.websiteSource === "deduced_from_email" && <span className="badge badge-orange" style={{marginLeft:6,fontSize:10}}>dedotto</span>}
                        {c.websiteSource === "manual" && <span className="badge badge-brand" style={{marginLeft:6,fontSize:10}}>manuale</span>}
                      </div>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-small">
                    {c.email ? (
                      <div>
                        <div style={{color:"var(--mv-success)"}}>{c.email}</div>
                        <EmailVerifyBadge company={c}/>
                      </div>
                    ) :
                     c.pec ? <div className="text-muted" title={c.pec}>PEC: {c.pec.length>22 ? c.pec.slice(0,22)+"…" : c.pec}</div> :
                     <span className="text-muted">—</span>}
                  </td>
                  <td className="text-small">{c.phone || <span className="text-muted">—</span>}</td>
                  <td className="text-small">{c.revenue ? `€ ${fmt(c.revenue)}` : <span className="text-muted">—</span>}</td>
                  <td className="text-small">
                    {c.inListsCount > 0 ? <span className="badge badge-brand">{c.inListsCount}</span> : <span className="text-muted">—</span>}
                  </td>
                  <td onClick={e=>e.stopPropagation()} style={{whiteSpace:"nowrap"}}>
                    <button onClick={()=>setSelectedId(c.id)} className="btn btn-ghost btn-sm"
                      title="Apri scheda" data-testid={`crm-view-${c.id}`}>
                      <Eye size={14}/>
                    </button>
                    <button onClick={()=>deleteOne(c.id, c.companyName)} className="btn btn-ghost btn-sm"
                      title="Elimina dal CRM"
                      data-testid={`crm-delete-${c.id}`}
                      style={{color:"var(--mv-danger)"}}>
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && total > companies.length && (
          <div className="flex justify-between items-center" style={{padding:14}}>
            <button className="btn btn-secondary btn-sm" disabled={skip===0} onClick={()=>load(Math.max(0,skip-50))}>← Precedenti</button>
            <div className="text-small text-muted">{skip+1} – {Math.min(skip+companies.length, total)} di {fmt(total)}</div>
            <button className="btn btn-secondary btn-sm" disabled={skip+companies.length>=total} onClick={()=>load(skip+50)}>Successivi →</button>
          </div>
        )}
      </div>

      {selectedId && (
        <CrmCompanyCard
          companyId={selectedId}
          onClose={()=>setSelectedId(null)}
          onUpdated={()=>load(skip)}
        />
      )}

      {enrichResult && (
        <div className="modal-backdrop" onClick={()=>setEnrichResult(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} data-testid="enrich-result-modal">
            <div className="flex justify-between items-center mb-4">
              <div className="h-section">Arricchimento completato</div>
              <button onClick={()=>setEnrichResult(null)} className="btn btn-ghost btn-sm"><X size={16}/></button>
            </div>
            <div className="text-small text-muted mb-4">Elaborate <strong>{enrichResult.processed}</strong> aziende</div>
            {enrichResult._verify ? (
              <div className="card" style={{padding:14}}>
                <div className="stat-label flex items-center gap-2"><ShieldCheck size={12}/> Verifica email (ZeroBounce)</div>
                <div className="text-small mt-2" style={{lineHeight:1.7}}>
                  <span style={{color:"var(--mv-success)"}}>● {enrichResult.verified || 0} verificate ora</span><br/>
                  <span className="text-muted">○ {enrichResult.cached || 0} dalla cache (&lt;60gg)</span><br/>
                  <span className="text-muted">○ {enrichResult.no_email || 0} senza email da verificare</span><br/>
                  {enrichResult.errors > 0 && <><span style={{color:"var(--mv-danger)"}}>● {enrichResult.errors} errori</span><br/></>}
                </div>
                {enrichResult.by_status && Object.keys(enrichResult.by_status).length > 0 && (
                  <div className="text-small mt-3" style={{lineHeight:1.7}}>
                    <strong>Risultati per status:</strong><br/>
                    {Object.entries(enrichResult.by_status).map(([s, n]) => (
                      <span key={s} style={{display:"inline-block",marginRight:10}}>
                        <strong>{s}</strong>: {n}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : enrichResult._delete ? (
              <div className="card" style={{padding:14}}>
                <div className="stat-label flex items-center gap-2"><Trash2 size={12}/> Eliminazione</div>
                <div className="text-small mt-2">
                  <strong>{enrichResult.deleted}</strong> aziende eliminate dal CRM (cascata: liste, analisi, email).
                </div>
              </div>
            ) : enrichResult._scrape ? (
              <div className="card" style={{padding:14}}>
                <div className="stat-label flex items-center gap-2"><Globe size={12}/> Scraping gratuito siti web</div>
                <div className="text-small mt-2" style={{lineHeight:1.7}}>
                  <span style={{color:"var(--mv-success)"}}>● {enrichResult.scraped_ok} siti con email trovate</span><br/>
                  <span className="text-muted">○ {enrichResult.scraped_skip} già scrapate (skip cache)</span><br/>
                  <span style={{color:"var(--mv-danger)"}}>● {enrichResult.scraped_fail} senza email / non raggiungibili</span><br/>
                  <span style={{fontWeight:600}}>✉️ {enrichResult.emails_found} email totali trovate · {enrichResult.emails_promoted} promosse a email primaria</span>
                </div>
              </div>
            ) : enrichResult._findContacts ? (
              <div>
                <div className="grid gap-3 mb-3" style={{gridTemplateColumns:"1fr 1fr 1fr"}}>
                  <div className="card" style={{padding:14}}>
                    <div className="stat-label flex items-center gap-2"><Globe size={12}/> Web search (cascata)</div>
                    <div className="text-small mt-2" style={{lineHeight:1.7}}>
                      <span style={{color:"var(--mv-success)"}}>● {enrichResult.web_search_hit} siti trovati</span><br/>
                      <span style={{color:"var(--mv-danger)"}}>● {enrichResult.web_search_miss} non trovati</span><br/>
                      <span className="text-muted">○ {enrichResult.web_search_skip_have_site} avevano già un sito</span>
                    </div>
                    <div className="text-small mt-2" style={{color:"#888"}}>
                      Google: <strong>{enrichResult.by_source?.google_places || 0}</strong> ·
                      Brave: <strong>{enrichResult.by_source?.brave_search || 0}</strong> ·
                      Guess: <strong>{enrichResult.by_source?.domain_guess || 0}</strong>
                    </div>
                  </div>
                  <div className="card" style={{padding:14}}>
                    <div className="stat-label flex items-center gap-2"><Mail size={12}/> OpenAPI fallback</div>
                    <div className="text-small mt-2" style={{lineHeight:1.7}}>
                      <span style={{color:"var(--mv-success)"}}>● {enrichResult.openapi_ok} arricchite</span><br/>
                      <span className="text-muted">○ {enrichResult.openapi_no_data} senza dati</span><br/>
                      <span className="text-muted">○ {enrichResult.openapi_skip_cache} cache (skip)</span><br/>
                      <span className="text-muted">○ {enrichResult.openapi_skip_disabled} disabilitata</span>
                    </div>
                  </div>
                  <div className="card" style={{padding:14}}>
                    <div className="stat-label flex items-center gap-2"><Sparkles size={12}/> Scrape email</div>
                    <div className="text-small mt-2" style={{lineHeight:1.7}}>
                      <span style={{color:"var(--mv-success)"}}>● {enrichResult.scraped_ok} con email</span><br/>
                      <span style={{color:"var(--mv-danger)"}}>● {enrichResult.scraped_fail} senza email</span><br/>
                      <span style={{fontWeight:600}}>✉️ {enrichResult.emails_promoted} email primarie</span><br/>
                      <span className="text-muted">📞 {enrichResult.phones_added} telefoni</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid gap-3" style={{gridTemplateColumns:"1fr 1fr"}}>
                <div className="card" style={{padding:14}}>
                  <div className="stat-label flex items-center gap-2"><Mail size={12}/> Email/Sito/Telefono</div>
                  <div className="text-small mt-2" style={{lineHeight:1.7}}>
                    <span style={{color:"var(--mv-success)"}}>● {enrichResult.marketing_ok || 0} arricchite</span><br/>
                    <span className="text-muted">○ {enrichResult.marketing_skip || 0} skip cache (già pagate)</span><br/>
                    <span className="text-muted">○ {enrichResult.marketing_no_data || 0} senza dati su OpenAPI</span><br/>
                    <span style={{color:"var(--mv-danger)"}}>● {enrichResult.marketing_fail || 0} errori</span>
                  </div>
                </div>
                <div className="card" style={{padding:14}}>
                  <div className="stat-label flex items-center gap-2"><UserCheck size={12}/> Titolari/Soci</div>
                  <div className="text-small mt-2" style={{lineHeight:1.7}}>
                    <span style={{color:"var(--mv-success)"}}>● {enrichResult.stakeholders_ok || 0} arricchite</span><br/>
                    <span className="text-muted">○ {enrichResult.stakeholders_skip || 0} skip cache</span><br/>
                    <span className="text-muted">○ {enrichResult.stakeholders_no_data || 0} senza dati</span><br/>
                    <span style={{color:"var(--mv-danger)"}}>● {enrichResult.stakeholders_fail || 0} errori</span>
                  </div>
                </div>
              </div>
            )}
            {enrichResult.credit_exhausted && (
              <div className="card mt-4" style={{background:"var(--mv-danger-bg)",borderLeft:"4px solid var(--mv-danger)",padding:14}}>
                <div style={{fontWeight:700,color:"var(--mv-danger)",marginBottom:6}}>⚠️ Credito OpenAPI insufficiente</div>
                <div className="text-small" style={{lineHeight:1.6}}>
                  {enrichResult.last_error}<br/>
                  Vai su <a href="https://console.openapi.com" target="_blank" rel="noreferrer" style={{color:"var(--mv-primary)",fontWeight:600}}>console.openapi.com</a> per ricaricare crediti o aggiungere gli scope <code>IT-marketing</code> e <code>IT-stakeholders</code> al tuo piano.
                </div>
              </div>
            )}
            {enrichResult.websites_deduced > 0 && (
              <div className="badge badge-orange mt-4" style={{display:"block",padding:"10px 14px"}}>
                💡 {enrichResult.websites_deduced} siti web dedotti automaticamente dal dominio dell'email
              </div>
            )}
            <button onClick={()=>setEnrichResult(null)} className="btn btn-primary w-full mt-6">Chiudi</button>
          </div>
        </div>
      )}

      {/* === Budget guardrail modal === */}
      {estimateModal && (
        <div className="modal-backdrop" onClick={()=>setEstimateModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} data-testid="enrich-estimate-modal">
            <div className="flex justify-between items-center mb-4">
              <div className="h-section">💰 Stima costo arricchimento</div>
              <button onClick={()=>setEstimateModal(null)} className="btn btn-ghost btn-sm"><X size={16}/></button>
            </div>
            <div className="text-small text-muted mb-4">
              {checkedIds.length} aziende selezionate. Le aziende già arricchite vengono <strong>saltate gratis grazie alla cache</strong>.
            </div>
            <div className="grid gap-3" style={{gridTemplateColumns:"1fr 1fr"}}>
              <div className="card" style={{padding:14}}>
                <div className="stat-label flex items-center gap-2"><Mail size={12}/> Email/Sito (IT-Marketing)</div>
                <div className="text-small mt-2" style={{lineHeight:1.8}}>
                  <span style={{color:"var(--mv-success)"}}>● {estimateModal.estimate.marketing_cached} in cache (gratis)</span><br/>
                  <span style={{color:"var(--mv-primary)",fontWeight:600}}>● {estimateModal.estimate.marketing_paid} da chiamare</span>
                </div>
              </div>
              {estimateModal.includeStakeholders && (
                <div className="card" style={{padding:14}}>
                  <div className="stat-label flex items-center gap-2"><UserCheck size={12}/> Titolari (IT-Stakeholders)</div>
                  <div className="text-small mt-2" style={{lineHeight:1.8}}>
                    <span style={{color:"var(--mv-success)"}}>● {estimateModal.estimate.stakeholders_cached} in cache (gratis)</span><br/>
                    <span style={{color:"var(--mv-primary)",fontWeight:600}}>● {estimateModal.estimate.stakeholders_paid} da chiamare</span>
                  </div>
                </div>
              )}
            </div>
            <div className="card mt-4" style={{padding:18,background:"var(--mv-primary-bg, #fff5ed)",borderLeft:"4px solid var(--mv-primary)"}}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-small text-muted">Costo stimato totale</div>
                  <div style={{fontSize:28,fontWeight:700,color:"var(--mv-primary)"}}>
                    € {estimateModal.estimate.estimated_cost_eur.toFixed(2)}
                  </div>
                </div>
                <div className="text-small text-muted" style={{textAlign:"right"}}>
                  {estimateModal.estimate.total_paid_calls} chiamate API<br/>
                  × € {estimateModal.estimate.cost_per_call_eur.toFixed(2)}/ciascuna
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={()=>setEstimateModal(null)} className="btn btn-secondary" style={{flex:1}} data-testid="estimate-cancel-btn">Annulla</button>
              <button onClick={confirmBulkEnrich} className="btn btn-primary" style={{flex:2}} data-testid="estimate-confirm-btn"
                disabled={estimateModal.estimate.total_paid_calls === 0}>
                {estimateModal.estimate.total_paid_calls === 0
                  ? "Tutto già in cache (nulla da fare)"
                  : `Conferma e spendi € ${estimateModal.estimate.estimated_cost_eur.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Find contacts (web-search-first cascade) modal === */}
      {findModal && (
        <div className="modal-backdrop" onClick={()=>setFindModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} data-testid="find-contacts-modal">
            <div className="flex justify-between items-center mb-4">
              <div className="h-section">🔎 Trova contatti completi</div>
              <button onClick={()=>setFindModal(null)} className="btn btn-ghost btn-sm"><X size={16}/></button>
            </div>
            <div className="text-small text-muted mb-4" style={{lineHeight:1.7}}>
              <strong>Cascata multi-source</strong> per trovare sito + email senza dipendere da OpenAPI:
            </div>
            <div className="card mb-3" style={{padding:14}}>
              <div className="text-small" style={{lineHeight:1.9}}>
                <div className="flex items-center gap-2">
                  <span style={{
                    display:"inline-block",width:18,height:18,borderRadius:"50%",
                    background: findModal.estimate.sources_configured?.google_places ? "var(--mv-success)" : "#ccc",
                    color:"white",textAlign:"center",fontSize:11,lineHeight:"18px"
                  }}>1</span>
                  <strong>Google Places API</strong>
                  <span className="text-muted">{findModal.estimate.sources_configured?.google_places ? "· attivo" : "· non configurato (manca GOOGLE_PLACES_API_KEY)"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{
                    display:"inline-block",width:18,height:18,borderRadius:"50%",
                    background: findModal.estimate.sources_configured?.brave_search ? "var(--mv-success)" : "#ccc",
                    color:"white",textAlign:"center",fontSize:11,lineHeight:"18px"
                  }}>2</span>
                  <strong>Brave Search API</strong>
                  <span className="text-muted">{findModal.estimate.sources_configured?.brave_search ? "· attivo" : "· non configurato (manca BRAVE_SEARCH_API_KEY)"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{
                    display:"inline-block",width:18,height:18,borderRadius:"50%",
                    background:"var(--mv-success)",color:"white",
                    textAlign:"center",fontSize:11,lineHeight:"18px"
                  }}>3</span>
                  <strong>Domain guessing</strong>
                  <span className="text-muted">· sempre attivo, gratis</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span style={{
                    display:"inline-block",width:18,height:18,borderRadius:"50%",
                    background:"var(--mv-primary)",color:"white",
                    textAlign:"center",fontSize:11,lineHeight:"18px"
                  }}>+</span>
                  <strong>Scrape sito</strong>
                  <span className="text-muted">· gratis, trova le email sul sito recuperato</span>
                </div>
              </div>
            </div>

            <label className="flex gap-2 items-start" style={{cursor:"pointer",padding:14,
              border:"1px solid var(--mv-border)",borderRadius:8,background:useOpenapiFallback?"#fff5ed":"transparent"}}>
              <input type="checkbox" checked={useOpenapiFallback}
                onChange={e=>{
                  setUseOpenapiFallback(e.target.checked);
                  // Re-fetch estimate when toggling
                  api.post("/crm/companies/find-contacts-estimate", {
                    companyIds: checkedIds, useOpenapiFallback: e.target.checked,
                  }).then(r => setFindModal({ estimate: r.data }));
                }}
                data-testid="use-openapi-fallback"/>
              <div>
                <strong>Usa OpenAPI come ultimo fallback (a pagamento)</strong>
                <div className="text-small text-muted mt-1">
                  Per le aziende dove la cascata web fallisce, chiama IT-Marketing OpenAPI.
                  Le aziende già tentate vengono saltate (cache).
                </div>
              </div>
            </label>

            <div className="card mt-4" style={{padding:14}}>
              <div className="text-small" style={{lineHeight:1.8}}>
                <div className="flex justify-between"><span>Aziende selezionate</span><strong>{findModal.estimate.total}</strong></div>
                <div className="flex justify-between" style={{color:"var(--mv-success)"}}>
                  <span>● Già hanno un sito (skip web search)</span><strong>{findModal.estimate.already_have_website}</strong>
                </div>
                <div className="flex justify-between">
                  <span>● Da cercare via web (gratis)</span><strong>{findModal.estimate.needs_web_search}</strong>
                </div>
                {useOpenapiFallback && (
                  <>
                    <div className="flex justify-between text-muted">
                      <span>○ OpenAPI cache (skip)</span><strong>{findModal.estimate.openapi_cached_skip}</strong>
                    </div>
                    <div className="flex justify-between" style={{color:"var(--mv-primary)",fontWeight:600}}>
                      <span>● Potenziali OpenAPI da chiamare</span><strong>{findModal.estimate.openapi_will_call}</strong>
                    </div>
                  </>
                )}
              </div>
            </div>

            {useOpenapiFallback && findModal.estimate.openapi_will_call > 0 && (
              <div className="card mt-3" style={{padding:14,background:"var(--mv-primary-bg, #fff5ed)",borderLeft:"4px solid var(--mv-primary)"}}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-small text-muted">Costo OpenAPI MASSIMO</div>
                    <div style={{fontSize:24,fontWeight:700,color:"var(--mv-primary)"}}>
                      € {findModal.estimate.estimated_cost_eur.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-small text-muted" style={{textAlign:"right"}}>
                    Solo se la cascata web<br/>
                    fallisce per quella azienda.<br/>
                    In pratica costerà molto meno.
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button onClick={()=>setFindModal(null)} className="btn btn-secondary" style={{flex:1}} data-testid="find-cancel-btn">Annulla</button>
              <button onClick={confirmFindContacts} className="btn btn-primary" style={{flex:2}} data-testid="find-confirm-btn">
                🚀 Avvia ricerca contatti
              </button>
            </div>
          </div>
        </div>
      )}

      {showCsvImport && (
        <CsvImportModal
          onClose={()=>setShowCsvImport(false)}
          onImported={()=>{
            setShowCsvImport(false);
            load(0); // reload from first page
            setSkip(0);
          }}
        />
      )}
    </div>
  );
}



const VERIFY_BADGE_STYLES = {
  valid: { bg: "#dcfce7", color: "#15803d", icon: "✓", label: "Valida" },
  invalid: { bg: "#fee2e2", color: "#991b1b", icon: "✗", label: "Non valida" },
  "catch-all": { bg: "#fef3c7", color: "#92400e", icon: "?", label: "Catch-all" },
  unknown: { bg: "#e5e7eb", color: "#374151", icon: "?", label: "Sconosciuta" },
  spamtrap: { bg: "#fee2e2", color: "#991b1b", icon: "⚠", label: "Spam-trap" },
  abuse: { bg: "#fee2e2", color: "#991b1b", icon: "⚠", label: "Abuso" },
  do_not_mail: { bg: "#fef3c7", color: "#92400e", icon: "✋", label: "Non inviare" },
};

function EmailVerifyBadge({ company }) {
  const status = company.emailVerifiedStatus;
  if (!status) return null;
  const s = VERIFY_BADGE_STYLES[status] || VERIFY_BADGE_STYLES.unknown;
  const subPart = company.emailVerifiedSubStatus ? ` · ${company.emailVerifiedSubStatus}` : "";
  return (
    <span
      title={`Verificata via ZeroBounce${subPart}\nScore: ${company.emailVerifiedScore || 0}`}
      style={{
        display: "inline-block", marginTop: 3, padding: "1px 7px", borderRadius: 4,
        fontSize: 10, fontWeight: 600,
        background: s.bg, color: s.color,
      }}>
      {s.icon} {s.label}
    </span>
  );
}
